import {
  C4_BOT,
  type C4Settings,
  type C4Snapshot,
  CPU_EDGE_CELLS,
  CPU_PLAYER,
  type GameId,
  nextVanishingCell,
  type PlayerPublic,
  REACTION_SETS,
  type Reaction,
  type Role,
  type RoomSettings,
  type RoomSnapshot,
} from '@xox/shared'
import type { ComponentType } from 'react'
import { C4Board } from '../../c4/C4Board'
import {
  C4SettingsForm,
  describeC4Settings,
  loadC4Settings,
  saveC4Settings,
} from '../../c4/C4SettingsForm'
import { duelApi } from '../../lib/api'
import { useC4Room } from '../../store/c4Room'
import type { DuelStoreHook, SnapshotOf } from '../../store/duel'
import { useRoom } from '../../store/room'
import { Board } from '../Board'
import {
  describeSettings,
  loadRoomSettings,
  RoomSettingsForm,
  saveRoomSettings,
} from '../RoomSettingsForm'
import type { SettingsFormProps } from '../TurnSecondsField'

interface DuelBoardProps<Snap> {
  snap: Snap
  role: Role
  canPlay: boolean
  pending: number | null
  onMove: (intent: number) => void
}

export type DuelSettingsBase = { turnSeconds: number }

/** Props of every generic duel page: the game it renders. */
export interface DuelPageProps<Snap extends SnapshotOf<S>, S extends DuelSettingsBase> {
  def: DuelGameDef<Snap, S>
}

/**
 * Everything the generic duel pages need to know about one two-seat game. Add a game here and
 * route it in App.tsx; the pages, store plumbing and bot chrome come for free.
 */
export interface DuelGameDef<Snap extends SnapshotOf<S>, S extends { turnSeconds: number }> {
  id: GameId
  home: '/xox' | '/c4'
  roomPath: '/r' | '/c4/r'
  title: string
  bot: PlayerPublic
  boss: {
    eyebrow: string
    title: string
    /** Shown under "You start"; the house rule when it is on, or a fair warning. */
    startNote: (edgeRule: boolean) => string | undefined
  }
  settings: {
    load: () => S
    save: (s: S) => void
    describe: (s: S) => string
    Form: ComponentType<SettingsFormProps<S>>
  }
  api: ReturnType<typeof duelApi<S>>
  store: DuelStoreHook<Snap>
  reactionSet: readonly Reaction[]
  seatStyle: 'xo' | 'disc'
  Board: ComponentType<DuelBoardProps<Snap>>
  copy: {
    /** Why the game ended when a line was made. */
    lineWin: string
    drawReason: string
    drawResult: string
  }
  /** "Your turn" replacement while the viewer is to move; null keeps the plain text. */
  turnHint?: (snap: Snap) => string | null
  /** A line above the board (XOX: the edge house rule while it applies). */
  boardNote?: (snap: Snap, role: Role) => string | null
}

function xoxHumanOpens(snap: RoomSnapshot, role: Role): boolean {
  const g = snap.game
  return snap.mode === 'cpu' && g.status === 'playing' && g.moveCount === 0 && role === g.turn
}

function xoxVanishing(snap: RoomSnapshot) {
  const g = snap.game
  const bothSeated = snap.seats.X !== null && snap.seats.O !== null
  return g.status === 'playing' && bothSeated && snap.settings.showVanishHint
    ? nextVanishingCell(g, g.turn)
    : null
}

function XoxBoard({ snap, role, canPlay, pending, onMove }: DuelBoardProps<RoomSnapshot>) {
  return (
    <Board
      game={snap.game}
      role={role}
      canPlay={canPlay}
      pendingCell={pending}
      vanishing={xoxVanishing(snap)}
      blockedCells={xoxHumanOpens(snap, role) && snap.edgeRule ? CPU_EDGE_CELLS : undefined}
      onMove={onMove}
    />
  )
}

export const XOX: DuelGameDef<RoomSnapshot, RoomSettings> = {
  id: 'xox',
  home: '/xox',
  roomPath: '/r',
  title: 'Vanishing XOX',
  bot: CPU_PLAYER,
  boss: {
    eyebrow: 'Boss fight',
    title: "You can't beat me!",
    startNote: (edgeRule) =>
      edgeRule ? "House rule: your first mark can't go on an edge." : undefined,
  },
  settings: {
    load: loadRoomSettings,
    save: saveRoomSettings,
    describe: describeSettings,
    Form: RoomSettingsForm,
  },
  api: duelApi<RoomSettings>('xox'),
  store: useRoom,
  reactionSet: REACTION_SETS.xox,
  seatStyle: 'xo',
  Board: XoxBoard,
  copy: {
    lineWin: 'Three in a row',
    drawReason: 'Repetition — nobody can force a win from here',
    drawResult: "A draw. That's as good as it gets.",
  },
  turnHint: (snap) =>
    xoxVanishing(snap) !== null ? 'Your turn — your faded mark vanishes when you play' : null,
  boardNote: (snap, role) =>
    xoxHumanOpens(snap, role) && snap.edgeRule ? 'You start. House rule: no edge openings.' : null,
}

function C4BoardAdapter({ snap, role, canPlay, pending, onMove }: DuelBoardProps<C4Snapshot>) {
  return (
    <C4Board game={snap.game} role={role} canPlay={canPlay} pending={pending} onMove={onMove} />
  )
}

export const C4: DuelGameDef<C4Snapshot, C4Settings> = {
  id: 'c4',
  home: '/c4',
  roomPath: '/c4/r',
  title: 'Connect Four',
  bot: C4_BOT,
  boss: {
    eyebrow: 'Boss fight',
    title: 'Four moves ahead.',
    startNote: () => 'Fair warning: whoever opens has the edge in Connect Four.',
  },
  settings: {
    load: loadC4Settings,
    save: saveC4Settings,
    describe: describeC4Settings,
    Form: C4SettingsForm,
  },
  api: duelApi<C4Settings>('c4'),
  store: useC4Room,
  // The disc-themed set is a later change; the marks pull faces for now.
  reactionSet: REACTION_SETS.xox,
  seatStyle: 'disc',
  Board: C4BoardAdapter,
  copy: {
    lineWin: 'Four in a row',
    drawReason: 'The board is full',
    drawResult: 'A draw. The board filled up.',
  },
}
